import React, { useState, useEffect } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import './AblationBenchmark.css';

const AblationBenchmark = () => {
  const axiosPrivate = useAxiosPrivate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBenchmarkData();
  }, []);

  const fetchBenchmarkData = async () => {
    try {
      setLoading(true);
      const response = await axiosPrivate.get('/AblationStudy/benchmarkAll');
      setData(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading benchmark results...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!data) return <div className="error">No data available</div>;

  return (
    <div className="ablation-benchmark-container">
      <header className="benchmark-header">
        <h1>🔬 Comprehensive Ablation Study: Clustering Algorithm Benchmark</h1>
        <div className="dataset-info">
          <span><strong>Paper Dataset:</strong> {data.paperDatasetSize} M&A Transactions</span>
          <span><strong>Processed:</strong> {data.actualProcessed} events</span>
          <span><strong>Date:</strong> {new Date(data.benchmarkDate).toLocaleString()}</span>
        </div>
      </header>

      {}
      <div className="benchmark-table-wrapper">
        <h2>📊 Algorithm Comparison Table</h2>
        <table className="benchmark-table">
          <thead>
            <tr>
              <th>Algorithm</th>
              <th>Description</th>
              <th>Silhouette<br/><small>(Higher = Better)</small></th>
              <th>DB Index<br/><small>(Lower = Better)</small></th>
              <th>Noise %<br/><small>(Lower = Better)</small></th>
              <th>Clusters</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.algorithms && data.algorithms.map((alg, idx) => (
              <tr
                key={idx}
                className={alg.status && alg.status.includes('RECOMMENDED') ? 'recommended-row' : ''}
              >
                <td className="algorithm-name">{alg.name}</td>
                <td className="algorithm-desc">{alg.description}</td>
                <td className={getSilhouetteClass(alg.silhouette)}>
                  <strong>{typeof alg.silhouette === 'number' ? alg.silhouette.toFixed(3) : alg.silhouette}</strong>
                  {alg.silhouette < 0 && <span className="warning-badge">⚠️ Negative</span>}
                </td>
                <td className={getDBClass(alg.daviesBouldin)}>
                  {typeof alg.daviesBouldin === 'number' ? alg.daviesBouldin.toFixed(3) : alg.daviesBouldin}
                </td>
                <td className={getNoiseClass(alg.noisePercent)}>
                  {typeof alg.noisePercent === 'number' ? alg.noisePercent.toFixed(1) : alg.noisePercent}%
                </td>
                <td>{alg.numClusters}</td>
                <td>
                  <span className={`status-badge status-${getStatusColor(alg.status)}`}>
                    {alg.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {}
      {data.recommendedMethod && (
        <div className="recommended-method-card">
          <h2>✅ Recommended Method: {data.recommendedMethod.name}</h2>
          <div className="recommendation-content">
            <div className="justification-box">
              <h3>Justification:</h3>
              <pre>{data.recommendedMethod.justification}</pre>
            </div>
          </div>
        </div>
      )}

      {}
      {data.networkAnalysis && data.patentData && (
        <div className="additional-info-grid">
          <div className="info-card">
            <h3>🌐 Network Analysis</h3>
            <p>{data.networkAnalysis.implementation}</p>
            {data.networkAnalysis.note && data.networkAnalysis.note.match(/http[^\s]+/) && (
              <a href={data.networkAnalysis.note.match(/http[^\s]+/)[0]} target="_blank" rel="noopener noreferrer">
                View Network Graph →
              </a>
            )}
          </div>
          <div className="info-card">
            <h3>📊 Patent Data Integration</h3>
            <p><strong>Source:</strong> {data.patentData.source}</p>
            <p><strong>API Key:</strong> {data.patentData.apiKey.substring(0, 12)}...</p>
          </div>
        </div>
      )}

      {}
      {data.algorithms && (
        <div className="verdicts-summary">
          <h2>📋 Algorithm Verdicts</h2>
          <div className="verdicts-grid">
            {data.algorithms.map((alg, idx) => (
              <div key={idx} className="verdict-card">
                <h4>{alg.name}</h4>
                <p>{alg.verdict}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const getSilhouetteClass = (value) => {
  if (value < 0) return 'metric-bad';
  if (value > 0.5) return 'metric-good';
  return 'metric-moderate';
};

const getDBClass = (value) => {
  if (value > 2) return 'metric-bad';
  if (value < 1) return 'metric-good';
  return 'metric-moderate';
};

const getNoiseClass = (value) => {
  if (value > 50) return 'metric-bad';
  if (value < 20) return 'metric-good';
  return 'metric-moderate';
};

const getStatusColor = (status) => {
  if (status.includes('RECOMMENDED')) return 'recommended';
  if (status.includes('Baseline')) return 'baseline';
  if (status.includes('Experimental')) return 'experimental';
  return 'alternative';
};

export default AblationBenchmark;
